/**
 * Fetches bulk-self-schedule interview status for the prerequisite overlay.
 * GET /api_ats/v1/ats_applications/bulk/interview_status
 *
 * Returns the candidate set + the list of stage actions on the job
 * stage + the no-email bucket. The shape is identical to the legacy
 * `ScheduleAPI.getStageActionDetails` consumer — we keep the legacy
 * call site working until JobCandidates flips behind
 * `feSchedulingRevampV2`. See `bulkSelfSchedulePrerequisite/categorise.ts`
 * for the rule set that turns this response into eligible / no-email /
 * already-scheduled.
 *
 * The `BulkSelfSchedule*` prefix on every exported name is deliberate
 * — the codebase has unrelated bulk modules (`BulkOperationQueue`,
 * legacy `bulk-manual` Scheduler, `BulkHireflixAssessmentDrawer`) and a
 * plain `Bulk*` name was confusing in cross-module greps.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/request';

export interface BulkSelfScheduleApplicationContact {
  id: number;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email?: string;
  image_url?: string;
  avatar_url?: string;
  avatar_thumb_url?: string;
  position?: string;
}

export interface BulkSelfScheduleApplication {
  id: number;
  contact: BulkSelfScheduleApplicationContact;
}

export interface BulkSelfScheduleStageAction {
  id: number;
  /**
   * Display name for the dropdown. Backend returns this under `label`
   * (legacy `<KSelect options={stageActions}>` relied on react-select's
   * default `getOptionLabel = option.label`); a few historic callers
   * surface it under `name` instead. The overlay reads both — see
   * `BulkSelfSchedulePrerequisiteOverlay.stageActionLabel`.
   */
  label?: string;
  name?: string;
  /**
   * Applications that already have this interview booked or completed
   * — surfaced to the user as a "won't receive the link" warning and
   * removed from the eligible set. Returned per stage action because
   * the same candidate may be eligible for one action and ineligible
   * for another.
   */
  existing_interview_applications?: BulkSelfScheduleApplication[];
}

export interface BulkSelfScheduleInvalidApplicationsBucket {
  reason?: string;
  ats_applications: BulkSelfScheduleApplication[];
}

/**
 * Raw shape returned by `GET /ats_applications/bulk/interview_status`.
 * Mirrors the legacy KInterviewScheduler consumer.
 *
 * Note: `ats_applications.contact` does **not** include `email`
 * server-side. The overlay joins email back in from the parent
 * table's `selectedApplications` data. See
 * `BulkSelfSchedulePrerequisiteOverlay.applicationIdToEmail`.
 */
export interface BulkSelfScheduleInterviewStatusResponse {
  ats_applications: BulkSelfScheduleApplication[];
  interview_job_stage_actions: BulkSelfScheduleStageAction[];
  /** `[ { reason: 'no_email', ats_applications: [...] } ]` — bucketed
   *  list. Today the only bucket the legacy code reads is index 0. */
  invalid_ats_applications?: BulkSelfScheduleInvalidApplicationsBucket[];
}

export interface UseBulkSelfScheduleInterviewStatusParams {
  selectedApplicationIds?: number[];
  unSelectedApplicationIds?: number[];
  jobStageId: number;
  jobStatus?: string;
}

async function fetchBulkSelfScheduleInterviewStatus(
  params: UseBulkSelfScheduleInterviewStatusParams
): Promise<BulkSelfScheduleInterviewStatusResponse> {
  const {
    selectedApplicationIds,
    unSelectedApplicationIds,
    jobStageId,
    jobStatus,
  } = params;
  return api.get<BulkSelfScheduleInterviewStatusResponse>(
    'ats_applications/bulk/interview_status',
    {
      params: {
        ats_application_ids: !unSelectedApplicationIds
          ? selectedApplicationIds
          : undefined,
        ats_job_stage_id: jobStageId,
        bulk_selected_ats_job_stage_ids: unSelectedApplicationIds && [
          jobStageId,
        ],
        bulk_selected_application_statuses: unSelectedApplicationIds &&
          jobStatus && [jobStatus],
        bulk_exempted_application_ids: unSelectedApplicationIds,
      },
    }
  );
}

export function useBulkSelfScheduleInterviewStatus(
  params: UseBulkSelfScheduleInterviewStatusParams,
  options?: { enabled?: boolean }
): UseQueryResult<BulkSelfScheduleInterviewStatusResponse, Error> {
  return useQuery({
    queryKey: [
      'schedule-interview',
      'bulk-self-schedule-interview-status',
      params,
    ],
    queryFn: () => fetchBulkSelfScheduleInterviewStatus(params),
    enabled: options?.enabled ?? true,
    staleTime: 0,
  });
}
